import React, { useMemo, useState } from "react";
import { match, Route } from "react-router-dom";
import useAppContext from "../../../context/useAppContext";
import useWebhookLogs from "../../utils/useWebhookLogs";
import EmptyState from "../table/EmptyState";
import WebhookLogEntryRow from "../table/WebhookLogEntryRow";
import LogSearchUpgradeBanner from "../shared/LogSearchUpgradeBanner";
import SearchBar from "../searchbar/SearchBar";
import MergeTable from "../../../shared/MergeTable";
import TableSkeletonLoader from "../../shared/TableSkeletonLoader";
import { isBillingPlanLaunchPlan } from "../../settings/billing/BillingUtils";
import { LinkedAccount, UserType } from "../../../../models/Entities";
import { LogSidePanel } from "../side-panel/shared-components/LogSidePanel";
import { fetchWebhookLogEntryByID } from "../../utils/IntegrationsManagementAPIClient";

interface Props {
  match: match;
  linkedAccount?: LinkedAccount | null;
}

/**
 * The whole of the Webhooks Logs tab is contained within this element. Allows searching,
 * paging, etc of all current logs. Title of the page comes in from elsewhere.
 */
const WebhooksLogsPage = ({ match, linkedAccount }: Props) => {
  const { paginationComponent, logs, isLoading, allFilters, applyFilters, setCurrentPageToken } =
    useWebhookLogs({ linkedAccount });

  const { user } = useAppContext();
  const privilegedRoles = new Set([
    UserType.admin_with_billing,
    UserType.admin_without_billing,
    UserType.support,
  ]);

  // Each search pill needs an associated focus value - only the last interacted should keep focus in most cases - keyed by id
  const [searchPillFocusId, setSearchPillFocusId] = useState<string | null>(null);

  const header = (
    <>
      <th scope="col">Method</th>
      <th scope="col">URL</th>
      <th scope="col">Direction</th>
      <th scope="col">Status</th>
      <th scope="col">Webhook type</th>
      <th scope="col">Event</th>
      {!linkedAccount && <th scope="col">Integration</th>}
      {!linkedAccount && <th scope="col">Organization</th>}
      <th scope="col">Date</th>
      <th style={{ width: "24px" }} />
    </>
  );

  const content = useMemo(() => {
    if (isLoading) {
      return <TableSkeletonLoader cols={linkedAccount ? 8 : 10} rows={10} />;
    } else if (!logs || logs.length <= 0) {
      return <EmptyState />;
    } else {
      // Make sure we close any focus when we push, otherwise coming back opens a dropdown
      return logs.map((log) => (
        <WebhookLogEntryRow key={log.id} log={log} match={match} linkedAccount={linkedAccount} />
      ));
    }
  }, [isLoading, linkedAccount, logs, match]);

  return (
    <>
      {isBillingPlanLaunchPlan(user.organization.organization_billing_plan?.billing_plan) &&
        !privilegedRoles.has(user.type) && <LogSearchUpgradeBanner />}
      <div className={linkedAccount ? "mb-6" : "mb-9"}>
        <SearchBar
          linkedAccount={linkedAccount}
          allFilters={allFilters}
          executeSearch={applyFilters}
          filterSearchesBySearchTerm={() => {}}
          searchPillFocusId={searchPillFocusId}
          setSearchPillFocusId={setSearchPillFocusId}
          setCurrentPageToken={setCurrentPageToken}
        />
      </div>
      <MergeTable
        header={header}
        content={content}
        hasMorePaddingOnFirstElement
        shouldMaskFrigade
      />
      {paginationComponent}
      <Route exact path={`${match.url}/:logID`}>
        {({ match: logMatch }) => (
          <LogSidePanel
            title="Webhook log"
            logID={logMatch?.params.logID}
            fetchLog={fetchWebhookLogEntryByID}
          />
        )}
      </Route>
    </>
  );
};

export default WebhooksLogsPage;
